import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { BaseUserDTO } from '@sb-tools/typescript/models/dto/base-user.dto';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NotificationStatusChangedEventModel } from '../../../core/models/dto/signalR/notification-status-changed-event-model.model';
import { NotificationDTO } from '../../../core/models/dto/notification.dto';
import { NotificationGroupType } from '../../../core/enums/notification-group-type.enum';
import { NotificationType } from '../../../core/enums/notification-type.enum';
import { RightPanelService } from '../../../core/services/right-panel.service';
import { NoteService } from '../../../core/services/note.service';
import { NotificationService } from '../../../core/services/notification.service';
import { SignalRService } from '../../../core/services/signalR.service';
import { RightPanelType } from '../../../core/enums/right-panel-type.enum';
import { NotificationDeletedEventModel } from '../../../core/models/dto/signalR/notification-deleted-event-model.model';
import { NoteNotificationMessage } from '../../../core/models/dto/note-notification-message.dto';
import { ReplyNotificationMessage } from '../../../core/models/dto/reply-notification-message.dto';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { UserAvatarComponent } from '../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { NgClass } from '@angular/common';
import { HighlightMentionComponent } from '../highlight-mention/highlight-mention.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { LoaderComponent } from '../../../../sb-tools/typescript/components/loader/loader.component';
import { TranslateModule } from '@ngx-translate/core';
import { TicketTimePipe } from '../../../../sb-tools/typescript/pipes/ticket/ticket-time.pipe';
import { NotificationExplanatoryTextPipe } from '../../pipes/notification-explanatory-text.pipe';

@Component({
    selector: 'app-notification-list',
    templateUrl: './notification-list.component.html',
    styleUrls: ['./notification-list.component.scss'],
    imports: [
        InfiniteScrollModule,
        UserAvatarComponent,
        InlineSVGComponent,
        NgClass,
        HighlightMentionComponent,
        TooltipModule,
        LoaderComponent,
        TranslateModule,
        TicketTimePipe,
        NotificationExplanatoryTextPipe,
    ],
})
export class NotificationListComponent implements OnInit, OnChanges, OnDestroy {
    @Input() infiniteScrollContainerClass: string;
    @Input() isShowUnread: boolean;
    @Input() notificationGroup?: NotificationGroupType;
    @Input() notificationTypes?: NotificationType[];
    notificationTypeEnum = NotificationType;

    @Output() needToCloseDropdownEvent = new EventEmitter<void>();

    areAllNotificationsLoaded: boolean;
    isLoading: boolean;
    notifications: NotificationDTO[] = [];

    private isMarkingAsRead: boolean;
    private skipCount = 0;
    private takeCount = 20;
    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        private rightPanelService: RightPanelService,
        private noteService: NoteService,
        private notificationService: NotificationService,
        private signalRService: SignalRService,
    ) {}

    ngOnInit() {
        this.initListeners();
    }

    ngOnChanges() {
        this.resetFilters();
        this.loadMore();
    }

    isNotificationWithAuthor(notification: NotificationDTO): boolean {
        return (
            !!notification.message &&
            notification.type !== NotificationType.ticketReminder &&
            notification.type !== NotificationType.replyReminder &&
            notification.type !== NotificationType.ticketSurveyResponseAdded &&
            notification.type !== NotificationType.emailDeliveryStatusUpdate
        );
    }

    getNotificationLink(notification: NotificationDTO): string {
        let href: string;
        if (this.isNotificationFromTheSameCommunity(notification)) {
            href = '/' + notification.communityName + '/tickets?';
        } else {
            href = window.location.origin + '/' + notification.communityName + '/tickets?';
        }

        href += this.getQueryParams(notification);

        return href;
    }

    loadMore(isReloading = false): void {
        if ((this.isLoading || this.areAllNotificationsLoaded) && !isReloading) {
            return;
        }

        this.isLoading = true;

        if (isReloading) {
            this.skipCount = 0;
            this.takeCount = this.notifications.length;
        } else {
            this.skipCount = this.notifications.length;
        }

        this.notificationService
            .getNotifications(
                this.skipCount,
                this.takeCount,
                this.notificationTypes,
                this.notificationGroup,
                this.isShowUnread,
            )
            .pipe(
                take(1),
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe((notifications: NotificationDTO[]) => {
                if (isReloading) {
                    this.notifications.length = 0; // Clear contents
                    this.notifications.push(...notifications);
                } else {
                    this.notifications = this.notifications.concat(notifications);
                }

                this.areAllNotificationsLoaded = notifications.length < this.takeCount;
            });
    }

    markAllAsRead(): void {
        if (this.isMarkingAsRead) {
            return;
        }

        this.isMarkingAsRead = true;

        this.notificationService
            .readAll()
            .pipe(
                take(1),
                finalize(() => {
                    this.isMarkingAsRead = false;
                }),
            )
            .subscribe(() => {
                this.loadMore(true);
            });
    }

    openNotification(event: Event, notification: NotificationDTO): void {
        event.preventDefault();

        // this.read(notification, true, () => {
        //     window.location.href = (event.target as HTMLElement).closest('a').getAttribute('href');
        // });

        this.read(notification, true, () => {
            window.location.href = (event.target as HTMLElement).closest('a').getAttribute('href');
        });

        // if (this.isNotificationFromTheSameCommunity(notification)) {
        //     this.read(notification, true);
        //     this.router.navigate(['/', this.commonDataService.communityName, '/tickets'],  {  queryParams: this.getQueryParamsAsObject(notification) });
        //     this.needToCloseDropdownEvent.emit();
        // } else {
        //     this.read(notification, true, () => {
        //         window.location.href = (event.target as HTMLElement).closest('a').getAttribute('href');
        //     });
        // }

        // todo: check what is it
        if (this.isNotificationFromNotes(notification) && this.isNotificationFromTheSameCommunity(notification)) {
            this.noteService.noteIdWithMentionedUser = (notification.message as NoteNotificationMessage).noteId;
            this.noteService.ignoreParamNoteId = true;
            this.noteService.disableNoteHighlighting = false;
        }
    }

    openUserInfo(author: BaseUserDTO): void {
        if (author) {
            this.rightPanelService.open(RightPanelType.user, { user: author });
        }
    }

    preventNavigation(event: Event): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    read(notification: NotificationDTO, status: boolean, callback?: () => void): void {
        this.notificationService
            .read(notification.id, status)
            .pipe(take(1))
            .subscribe((newStatus: boolean) => {
                if (notification.isRead !== newStatus) {
                    notification.isRead = newStatus;
                }

                if (this.isShowUnread && notification.isRead) {
                    this.removeNotificationFromList(notification);
                }

                if (callback) {
                    callback();
                }
            });
    }

    private getQueryParams(notification: NotificationDTO): string {
        switch (notification.type) {
            case NotificationType.reply:
            case NotificationType.subReply: {
                return (
                    'ticketId=' +
                    notification.ticketId +
                    '&replyId=' +
                    (notification.message as ReplyNotificationMessage).replyId
                );
            }
            case NotificationType.ticketReminder:
                return 'ticketId=' + notification.ticketId;
            case NotificationType.replyReminder:
                return (
                    'ticketId=' +
                    notification.ticketId +
                    ((notification.message as ReplyNotificationMessage).replyId
                        ? '&replyId=' + (notification.message as ReplyNotificationMessage).replyId
                        : '')
                );
            case NotificationType.ticketSurveyResponseAdded: {
                return 'ticketId=' + notification.ticketId;
            }
            case NotificationType.emailDeliveryStatusUpdate: {
                return 'ticketId=' + notification.ticketId + '&rightPanelTab=log';
            }
            default: {
                return (
                    'ticketId=' +
                    notification.ticketId +
                    '&noteId=' +
                    (notification.message as NoteNotificationMessage).noteId
                );
            }
        }
    }

    private getQueryParamsAsObject(notification: NotificationDTO): {
        ticketId: number;
        replyId?: number;
        noteId?: number;
    } {
        switch (notification.type) {
            case NotificationType.reply:
            case NotificationType.subReply: {
                return {
                    ticketId: notification.ticketId,
                    replyId: (notification.message as ReplyNotificationMessage).replyId,
                };
            }
            default: {
                return {
                    ticketId: notification.ticketId,
                    noteId: (notification.message as NoteNotificationMessage).noteId,
                };
            }
        }
    }

    private initListeners(): void {
        this.signalRService
            .onNotificationAdded()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.loadMore(true);
            });

        this.signalRService
            .onNotificationStatusUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((updatedNotification: NotificationStatusChangedEventModel) => {
                const notification = this.notifications.find((existedNotification) => {
                    return existedNotification.id === updatedNotification.notificationId;
                });

                if (notification) {
                    notification.isRead = updatedNotification.isRead;

                    if (this.isShowUnread && notification.isRead) {
                        this.removeNotificationFromList(notification);
                    }
                }
            });

        this.signalRService
            .onNotificationDeleted()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((deletedNotifications: NotificationDeletedEventModel) => {
                this.notifications = this.notifications.filter((notification) => {
                    return !deletedNotifications.notificationIds.find((deletedNotificationId) => {
                        return deletedNotificationId === notification.id;
                    });
                });
            });
    }

    private isNotificationFromNotes(notification: NotificationDTO): boolean {
        return [NotificationType.answerToNote, NotificationType.mentionInNote].includes(notification.type);
    }

    private isNotificationFromTheSameCommunity(notification: NotificationDTO): boolean {
        return notification.communityName === this.commonDataService.communityName;
    }

    private removeNotificationFromList(notification: NotificationDTO): void {
        const index = this.notifications.findIndex((existedNotification) => {
            return existedNotification.id === notification.id;
        });
        this.notifications.splice(index, 1);
    }

    private resetFilters(): void {
        this.skipCount = 0;
        this.notifications = [];
        this.isLoading = false;
        this.areAllNotificationsLoaded = false;
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
